import { Card, CardContent, CardHeader, CardTitle } from '@artstorefronts/ui';
import { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import type { TooltipContentProps } from 'recharts';

interface MetricPoint {
  period: string;
  value: number;
}

interface MetricSeries {
  count: number;
  series: MetricPoint[];
}

interface StatsCardChartProps {
  title: string;
  metric: MetricSeries;
  icon: LucideIcon;
  description: string;
}

function ChartTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  const { t } = useTranslation('social');
  if (!active || !payload) return null;

  return (
    <div className="rounded-lg border bg-white p-2 shadow-md">
      <div className="space-y-1">
        <p className="text-sm font-medium">{t('stats.period', { label })}</p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">{t('stats.value')}</span>
          <span className="font-medium">{payload[0]?.value?.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function StatsCardChart({ title, metric, icon: Icon, description }: StatsCardChartProps) {
  if (!metric) return null;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{metric.count.toLocaleString()}</div>
        <p className="text-xs text-muted-foreground mb-4">{description}</p>
        <div className="h-[80px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={metric.series} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="period" hide={true} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--background)', opacity: 0.1 }} />
              <Bar dataKey="value" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export default StatsCardChart;
